const jquerySource = 'https://code.jquery.com/jquery-3.7.1.min.js';
const slickSource = 'https://cdn.jsdelivr.net/npm/slick-carousel@1.8.1/slick/slick.min.js';
let dependenciesPromise;

function loadScript(source, isReady) {
  if (isReady()) return Promise.resolve();

  let script = [...document.querySelectorAll('script[data-product-list-slick-dependency]')].find(
    (element) => element.dataset.productListSlickDependency === source
  );

  if (!script) {
    script = document.createElement('script');
    script.src = source;
    script.async = true;
    script.dataset.productListSlickDependency = source;
    document.head.append(script);
  }

  return new Promise((resolve, reject) => {
    if (isReady()) {
      resolve();
      return;
    }

    script.addEventListener('load', () => {
      if (isReady()) {
        resolve();
      } else {
        reject(new Error(`Unable to initialize ${source}`));
      }
    }, { once: true });
    script.addEventListener('error', reject, { once: true });
  });
}

function loadDependencies() {
  dependenciesPromise ??= loadScript(jquerySource, () => typeof window.jQuery === 'function').then(() =>
    loadScript(slickSource, () => typeof window.jQuery?.fn?.slick === 'function')
  );

  return dependenciesPromise;
}

function escapeAttribute(value) {
  const element = document.createElement('span');
  element.textContent = value;
  return element.innerHTML.replaceAll('"', '&quot;');
}

function createArrow(className, label, symbol) {
  return `<button type="button" class="slick-arrow ${className}" aria-label="${escapeAttribute(label)}"><span aria-hidden="true">${symbol}</span></button>`;
}

function getCarousels(root) {
  const carousels = [];

  if (root instanceof Element && root.matches('[data-product-list-slick]')) {
    carousels.push(root);
  }

  if (root.querySelectorAll) {
    carousels.push(...root.querySelectorAll('[data-product-list-slick]'));
  }

  return carousels;
}

async function initializeProductLists(root = document) {
  try {
    await loadDependencies();
  } catch {
    return;
  }

  const $ = window.jQuery;
  if (typeof $?.fn?.slick !== 'function') return;

  getCarousels(root).forEach((carousel) => {
    if (!carousel.isConnected || carousel.classList.contains('slick-initialized')) return;

    const { dataset } = carousel;
    const desktopSlides = Math.max(1, Number.parseInt(dataset.slickDesktopSlides, 10) || 1);
    const mobileSlides = Math.max(1, Number.parseInt(dataset.slickMobileSlides, 10) || 1);
    const mobilePadding = Math.min(15, Math.max(0, Number.parseInt(dataset.slickMobilePadding, 10) || 0));
    const slideCount = Number.parseInt(dataset.slickSlideCount, 10) || 0;
    const loop = dataset.slickLoop !== 'false' && slideCount > 1;
    const arrows = dataset.slickArrows === 'true';

    $(carousel).slick({
      accessibility: true,
      arrows,
      centerMode: false,
      dots: false,
      draggable: true,
      infinite: loop,
      pauseOnFocus: true,
      pauseOnHover: true,
      prevArrow: createArrow('slick-prev', dataset.slickPrevLabel || 'Previous', '&lsaquo;'),
      nextArrow: createArrow('slick-next', dataset.slickNextLabel || 'Next', '&rsaquo;'),
      slidesToScroll: 1,
      slidesToShow: desktopSlides,
      speed: 350,
      swipe: true,
      swipeToSlide: true,
      touchMove: true,
      responsive: [
        {
          breakpoint: 750,
          settings: {
            centerMode: true,
            centerPadding: `${mobilePadding}%`,
            slidesToScroll: 1,
            slidesToShow: mobileSlides,
          },
        },
      ],
    });
  });
}

function destroyProductLists(root) {
  const $ = window.jQuery;
  if (typeof $?.fn?.slick !== 'function') return;

  getCarousels(root).forEach((carousel) => {
    if ($(carousel).hasClass('slick-initialized')) {
      $(carousel).slick('unslick');
    }
  });
}

function observeDynamicCarousels() {
  if (!document.body) return;

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (!node.matches('[data-product-list-slick]') && !node.querySelector('[data-product-list-slick]')) continue;
        initializeProductLists(node);
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

const initialize = () => initializeProductLists(document);

observeDynamicCarousels();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initialize, { once: true });
} else {
  initialize();
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) initializeProductLists(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  if (event.target instanceof HTMLElement) destroyProductLists(event.target);
});
